import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, MicOff, Send, Volume2, VolumeX, Square } from "lucide-react";
import { toast } from "sonner";
import { streamChat, fetchTTSAudio } from "@/lib/api";

const FALLBACK_CHIPS = [
  "What is a 360° virtual tour?",
  "How much does a virtual tour cost?",
  "What is multilingual dubbing?",
  "How do I book a strategy call?",
];

const now = () => new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

export const ChatInterface = ({ onUserMessage, onStatusChange }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [listening, setListening] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [voiceOn, setVoiceOn] = useState(true);
  const voiceOnRef = useRef(true);
  const [chips, setChips] = useState(FALLBACK_CHIPS);
  const listRef = useRef(null);
  const audioRef = useRef(null);
  const recogRef = useRef(null);
  const sessionRef = useRef(crypto.randomUUID());

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chips`)
      .then((r) => r.json())
      .then((d) => { if (Array.isArray(d.chips) && d.chips.length) setChips(d.chips); })
      .catch(() => {});
  }, []);

  // Spoken welcome: at most once per week per visitor, rotating through three lines
  // (browser autoplay rules require a user gesture before audio can play)
  useEffect(() => {
    const GREETINGS = [
      "Hi, I'm Revelle, from Revol Studios. Ask me anything about our 360 virtual tours, avatar creation, multilingual dubbing, or interactive video - or if you'd prefer a human, you can schedule a free strategy call.",
      "Welcome to Revol Studios, I'm Revelle. Whether it's virtual tours, avatars, dubbing or interactive video, just ask - and a free strategy call is always there if you'd rather talk to a human.",
      "Great to see you - Revelle here, from Revol Studios. I can answer anything about our immersive work in seconds, and the team is one free strategy call away if you'd like the human touch.",
    ];
    let state = {};
    try { state = JSON.parse(localStorage.getItem("revel_greeting") || "{}"); } catch { state = {}; }
    const WEEK = 7 * 24 * 60 * 60 * 1000;
    if (state.last && Date.now() - state.last < WEEK) return;
    const greet = () => {
      window.removeEventListener("pointerdown", greet);
      window.removeEventListener("keydown", greet);
      const idx = typeof state.idx === "number" ? (state.idx + 1) % GREETINGS.length : 0;
      localStorage.setItem("revel_greeting", JSON.stringify({ last: Date.now(), idx }));
      if (voiceOnRef.current) speakText(`greeting-${idx}`, GREETINGS[idx], { auto: true });
    };
    window.addEventListener("pointerdown", greet);
    window.addEventListener("keydown", greet);
    return () => {
      window.removeEventListener("pointerdown", greet);
      window.removeEventListener("keydown", greet);
    };
  }, []);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const setStatus = (s) => onStatusChange && onStatusChange(s);

  const send = (text) => {
    const question = (text ?? input).trim();
    if (!question || streaming) return;
    setInput("");
    onUserMessage && onUserMessage(question);
    const userMsg = { id: crypto.randomUUID(), role: "user", text: question, ts: now() };
    const agentMsg = { id: crypto.randomUUID(), role: "agent", text: "", ts: now(), streaming: true };
    setMessages((m) => [...m, userMsg, agentMsg]);
    setStreaming(true);
    setStatus("thinking");

    let acc = "";
    streamChat({
      message: question,
      sessionId: sessionRef.current,
      onToken: (t) => {
        acc += t;
        setStatus("speaking");
        setMessages((m) => m.map((x) => (x.id === agentMsg.id ? { ...x, text: x.text + t } : x)));
      },
      onReset: () => {
        acc = "";
        setMessages((m) => m.map((x) => (x.id === agentMsg.id ? { ...x, text: "" } : x)));
      },
      onDone: (data) => {
        setMessages((m) => m.map((x) => (x.id === agentMsg.id ? { ...x, streaming: false, category: data.category } : x)));
        setStreaming(false);
        setStatus("ready");
        if (data.audioReady && voiceOnRef.current && acc.trim()) {
          speakText(agentMsg.id, acc.trim(), { auto: true });
        }
      },
      onError: (err) => {
        setMessages((m) => m.map((x) => (x.id === agentMsg.id ? { ...x, text: err, streaming: false, error: true } : x)));
        setStreaming(false);
        setStatus("ready");
      },
    });
  };

  const stopAudio = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    window.speechSynthesis?.cancel();
    setPlayingId(null);
    setStatus("ready");
  };

  const speakText = async (id, text, { auto = false } = {}) => {
    if (playingId === id && !auto) return stopAudio();
    stopAudio();
    setPlayingId(id);
    setStatus("speaking");

    const audio = new Audio();
    audio.preload = "auto";
    audioRef.current = audio;
    audio.onended = () => { setPlayingId(null); setStatus("ready"); };
    audio.onerror = () => { setPlayingId(null); setStatus("ready"); };

    try {
      const url = await fetchTTSAudio(text);
      audio.src = url;
      await audio.play();
    } catch {
      if (auto) toast.info("Tap Listen to hear Revelle.");
      setPlayingId(null);
      setStatus("ready");
    }
  };

  const listen = (msg) => speakText(msg.id, msg.text);

  const toggleVoice = () => {
    const next = !voiceOn;
    setVoiceOn(next);
    voiceOnRef.current = next;
    if (!next) stopAudio();
  };

  const toggleMic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Voice dictation isn't supported in this browser - try Chrome.");
      return;
    }
    if (listening) {
      recogRef.current?.stop();
      return;
    }
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = true;
    rec.lang = "en-GB";
    rec.onresult = (e) => {
      const transcript = Array.from(e.results).map((r) => r[0].transcript).join("");
      setInput(transcript);
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => {
      setListening(false);
      toast.error("Couldn't hear you - please try again.");
    };
    recogRef.current = rec;
    rec.start();
    setListening(true);
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="relative flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0c]/80 shadow-2xl shadow-black/80 backdrop-blur-xl min-h-[480px] lg:min-h-[calc(100vh-180px)]"
      data-testid="chat-panel"
    >
      <div className="flex items-center justify-between border-b border-white/10 px-5 py-3.5">
        <div>
          <p className="text-sm font-semibold text-white">Intelligence Hub</p>
          <p className="text-xs text-[#a1a1aa]">Grounded in the Revol Studios knowledge base</p>
        </div>
        <div className="flex items-center gap-2.5">
          <span className="font-mono-brand text-[10px] uppercase tracking-widest text-[#ed4609]/70 border border-[#ed4609]/25 rounded-full px-3 py-1">
            {chips.length ? "Live" : ""} Knowledge Base
          </span>
          <button
            onClick={toggleVoice}
            data-testid="voice-toggle-button"
            aria-label={voiceOn ? "Mute Revelle's voice" : "Unmute Revelle's voice"}
            title={voiceOn ? "Voice on - answers read aloud" : "Voice off"}
            className={`rounded-lg p-2 transition-colors duration-200 ${
              voiceOn ? "bg-[#ed4609]/15 text-[#ed4609]" : "text-[#71717a] hover:text-white hover:bg-white/5"
            }`}
          >
            {voiceOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <div ref={listRef} className="chat-scroll flex-1 overflow-y-auto px-5 py-5 space-y-5" data-testid="chat-messages">
        {messages.length === 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="flex h-full flex-col items-center justify-center text-center" data-testid="chat-empty-state">
            <p className="text-xl font-semibold text-white">What would you like to know?</p>
            <p className="mt-2 max-w-md text-sm text-[#a1a1aa]">
              Ask about virtual tours, avatars, dubbing, interactive video, pricing or booking.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {chips.map((s, i) => (
                <motion.button
                  key={s}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.75 + i * 0.08 }}
                  onClick={() => send(s)}
                  data-testid={`suggestion-chip-${i}`}
                  className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-xs text-[#a1a1aa] transition-colors duration-200 hover:border-[#ed4609]/50 hover:text-white hover:bg-[#ed4609]/10"
                >
                  {s}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}

        <AnimatePresence initial={false}>
          {messages.map((msg) => (
            <motion.div
              key={msg.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              className={msg.role === "user" ? "flex justify-end" : "flex justify-start"}
              data-testid={msg.role === "user" ? "user-message" : "agent-message"}
            >
              {msg.role === "user" ? (
                <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-[#141418] border border-white/10 px-4 py-3">
                  <p className="text-sm text-white leading-relaxed">{msg.text}</p>
                  <p className="mt-1 text-right text-[10px] text-[#71717a]">{msg.ts}</p>
                </div>
              ) : (
                <div className="max-w-[92%] flex gap-3">
                  <div className="mt-1 h-full min-h-[40px] w-1 rounded-full bg-[#ed4609] shadow-[0_0_10px_rgba(237,70,9,0.5)]" />
                  <div className="rounded-2xl rounded-tl-sm border border-[#ed4609]/15 bg-[#ed4609]/5 px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-white">Revelle</span>
                      <span className="text-[10px] text-[#71717a]">{msg.ts}</span>
                      {msg.category && (
                        <span className="rounded-full bg-white/5 border border-white/10 px-2 py-0.5 text-[9px] text-[#a1a1aa]">{msg.category}</span>
                      )}
                    </div>
                    <p className="mt-1.5 whitespace-pre-line text-sm text-white leading-relaxed">
                      {msg.text}
                      {msg.streaming && <span className="stream-caret ml-0.5 inline-block h-3.5 w-[2px] bg-[#ed4609] align-middle" />}
                    </p>
                    {!msg.streaming && !msg.error && (
                      <button
                        onClick={() => listen(msg)}
                        data-testid="listen-audio-button"
                        aria-label={playingId === msg.id ? "Stop audio" : "Listen to this answer"}
                        className="mt-2 flex items-center gap-1.5 text-xs text-[#a1a1aa] transition-colors duration-200 hover:text-[#ed4609]"
                      >
                        {playingId === msg.id ? (
                          <>
                            <span className="flex items-end gap-[2px] h-3">
                              {[0, 1, 2, 3].map((i) => (
                                <span key={i} className="wave-bar w-[2px] h-3 bg-[#ed4609] rounded-full" style={{ animationDelay: `${i * 0.12}s` }} />
                              ))}
                            </span>
                            <Square className="h-3 w-3" /> Stop
                          </>
                        ) : (
                          <><Volume2 className="h-3.5 w-3.5" /> Listen</>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <div className="border-t border-white/10 p-4">
        <div className="flex items-end gap-2 rounded-xl border border-white/10 bg-black/40 px-3 py-2 transition-colors duration-200 focus-within:border-[#ed4609]/50">
          <button
            onClick={toggleMic}
            data-testid="mic-dictation-button"
            aria-label={listening ? "Stop dictation" : "Start voice dictation"}
            className={`rounded-lg p-2.5 transition-colors duration-200 ${
              listening ? "mic-recording bg-[#ed4609] text-white" : "text-[#a1a1aa] hover:text-[#ed4609] hover:bg-white/5"
            }`}
          >
            {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
          </button>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={listening ? "Listening..." : "Ask Revelle anything..."}
            rows={1}
            data-testid="chat-input"
            aria-label="Ask Revelle a question"
            className="max-h-32 flex-1 resize-none bg-transparent py-2 text-sm text-white placeholder-[#71717a] focus:outline-none"
          />
          <motion.button
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.94 }}
            onClick={() => send()}
            disabled={!input.trim() || streaming}
            data-testid="send-message-button"
            aria-label="Send message"
            className="rounded-lg bg-[#ed4609] p-2.5 text-white transition-shadow duration-200 hover:bg-[#ff571a] hover:shadow-[0_0_20px_rgba(237,70,9,0.4)] disabled:opacity-40 disabled:hover:shadow-none"
          >
            <Send className="h-4 w-4" />
          </motion.button>
        </div>
        <p className="mt-2 text-center text-[10px] text-[#71717a]">
          Answers are grounded in the Revol Studios knowledge base. For anything bespoke, book a free strategy call.
        </p>
      </div>
    </motion.section>
  );
};
